import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { focusDeferred } from "../../../../shared/hooks/useFocusOnMount";
import { createPortal } from "react-dom";
import { BootSkeleton } from "../components/BootSkeleton";
import { bootReleased, detectLocale, formatters, loadedSpanish, loadSpanish, setLocale, translateWith, type Locale, type MessageKey, type Spanish } from ".";

/**
 * Set by main.tsx at entry evaluation while a Spanish reader's catalogue is on
 * its way (rc.12 (b)): #root is mounted but not displayed, and the text-free
 * boot skeleton shows instead, so no English is ever painted or announced.
 * Removed by the provider once Spanish (or, on a failed load, English; the
 * release notice only if a newer release is live) has rendered.
 */
export const LOCALE_PENDING_CLASS = "l-locale-pending";

interface TranslationValue {
  locale: Locale;
  /**
   * A Spanish reader's catalogue is still on its way (rc.12 (b)); the page is
   * unpainted meanwhile. Part of the value so it changes when the catalogue
   * lands: every consumer then renders the Spanish.
   */
  pending: boolean;
  /**
   * The reader pressed Español and the catalogue is on its way (rc.12, the
   * Web Director): the switch shows the press as taken and busy, and a second
   * Español press does nothing. English presses stay instant.
   */
  switching: boolean;
  /**
   * The loaded Spanish catalogue's review copy, or null (rc.13): for
   * `reviewCopyFor`, which must read it from React state, not module state.
   */
  spanishReviewCopy: Spanish["reviewCopy"] | null;
  t(key: MessageKey, values?: Record<string, string | number>): string;
  fmt: ReturnType<typeof formatters>;
  chooseLocale(locale: Locale): void;
}

const TranslationContext = createContext<TranslationValue | null>(null);

export function TranslationProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  /** Injected in tests so a suite does not depend on the runner's navigator. */
  initialLocale?: Locale;
}) {
  // A Spanish reader's first load (rc.12 (b)): the locale is Spanish from the
  // start, so every data read asks in Spanish, while the page stays unpainted
  // (main.tsx's class) until the catalogue is here. If it fails: English, and
  // the page says so (lang, the switch); the stored choice stays Spanish.
  const [wanted] = useState<Locale>(() => initialLocale ?? detectLocale());
  const [locale, setLocaleState] = useState<Locale>(wanted);
  const [booting, setBooting] = useState(() => wanted === "es" && !loadedSpanish());
  // The Spanish catalogue `t` reads, as STATE: when it lands, `t` is a new
  // function, so every consumer (compiled ones cache `t(...)` by `t`) renders
  // the Spanish. Read from the module, `t` kept its identity and a compiled
  // consumer kept the text it had before (found by i18n/spanish.test.tsx).
  const [catalogue, setCatalogue] = useState<Spanish | null>(() => loadedSpanish());
  useEffect(() => {
    if (!booting) return;
    let live = true;
    // The load main.tsx started at entry evaluation (the same promise).
    // Revealed once a step chunk the screen needs has settled too
    // (holdBoot): one skeleton, then the step.
    void loadSpanish()
      .then(async (loaded) => {
        await bootReleased();
        return loaded;
      })
      .then((loaded) => {
        if (!live) return;
        if (loaded) setCatalogue(loadedSpanish());
        else setLocaleState("en");
        setBooting(false);
      });
    return () => {
      live = false;
    };
  }, [booting]);
  // Before paint, in the commit that already shows Spanish (or English with
  // the notice): never a painted English frame on a Spanish visit.
  // An arrival that focused its heading while the page was hidden gets that
  // focus now (focusDeferred): the reveal must not drop a reader on <body>.
  useLayoutEffect(() => {
    if (booting) return;
    const root = document.documentElement;
    if (!root.classList.contains(LOCALE_PENDING_CLASS)) return;
    root.classList.remove(LOCALE_PENDING_CLASS);
    focusDeferred();
  }, [booting]);
  // The reader's LATEST choice: a Spanish load that lands after they chose
  // English again must not switch the page (review of 9d281bf8, P2).
  const chosen = useRef<Locale>(wanted);
  const [switching, setSwitching] = useState(false);

  // useMemo rather than relying on the React Compiler: `fmt` builds four Intl
  // formatters, and those are genuinely expensive to construct. This is the
  // documented exception to the no-manual-memo rule, not a habit.
  const value = useMemo<TranslationValue>(
    () => ({
      locale,
      pending: booting,
      switching,
      spanishReviewCopy: catalogue?.reviewCopy ?? null,
      t: (key, values) => translateWith(catalogue, locale, key, values),
      fmt: formatters(locale),
      chooseLocale: (next: Locale) => {
        // Stored at once: a reload honours the choice even if the load fails.
        if (next === "es" && switching) return; // already on its way
        setLocale(next);
        chosen.current = next;
        if (next === "en" || loadedSpanish()) {
          if (next === "es") setCatalogue(loadedSpanish());
          setSwitching(false);
          setLocaleState(next);
          return;
        }
        // Spanish loads first; the page keeps English until it is here, with
        // the press shown as taken and busy. A failure ends the busy state on
        // English; the release notice only if a newer release is live
        // (loadSpanish, chunkFailureVerdict).
        setSwitching(true);
        void loadSpanish().then((loaded) => {
          if (chosen.current !== "es") return; // English since: it stands
          setSwitching(false);
          if (!loaded) return;
          setCatalogue(loadedSpanish());
          setLocaleState("es");
        });
      },
    }),
    // `catalogue`: the strings change when it lands, with the locale
    // unchanged, and `t` reads it. `booting` is read in the body (`pending`).
    [locale, booting, catalogue, switching]
  );

  // Applied here rather than exported as a hook nobody remembers to call. The
  // first version of this WAS a separate `useDocumentLocale` export, and it was
  // never wired up — same shape as the env guard that shipped in no bundle.
  //
  // Not cosmetic: screen readers pick pronunciation from `lang`, and a Spanish
  // page announced by an English voice is close to unusable. It also drives
  // hyphenation and quote rendering.
  useEffect(() => {
    if (document.documentElement.lang !== locale) {
      document.documentElement.lang = locale;
    }
  }, [locale]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
      {/* A Spanish reader's first load: the text-free frame, outside #root
          (which is mounted but not displayed meanwhile). */}
      {booting ? createPortal(<BootSkeleton />, document.body) : null}
    </TranslationContext.Provider>
  );
}

export function useTranslation(): TranslationValue {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error("useTranslation must be used inside <TranslationProvider>");
  }
  return context;
}
